const API_BASE = "/api";

// CSRF token received from server (set on login and /auth/me)
let csrfToken: string | null = null;

export function setCsrfToken(token: string | null) {
  csrfToken = token;
}

export class ApiError extends Error {
  status: number;
  errorCode?: string;
  headers?: string[];
  constructor(message: string, status: number, errorCode?: string, headers?: string[]) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errorCode = errorCode;
    this.headers = headers;
  }
}

/**
 * API が返していない本文 (CloudFront / WAF の HTML エラーページなど) を識別するコード。
 * 業務エラーコードと同じ名前空間に載せるが、サーバー側の code とは決して衝突しない。
 * 呼び出し側はこれを見て「利用者のファイルや入力の問題」と「当社側の遮断」を言い分ける。
 */
export const NON_JSON_ERROR_RESPONSE = "NON_JSON_ERROR_RESPONSE";

async function parseErrorResponse(res: Response): Promise<{ message?: string; error?: string; code?: string; headers?: string[] }> {
  const contentType = res.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    const body = await res.json().catch(() => null) as
      { message?: string; error?: string; code?: string; headers?: string[] } | null;
    if (body && typeof body === "object") return body;
    // JSON を宣言しながら本文が壊れている応答も、API の業務エラーとしては扱わない。
    return { message: res.statusText || `HTTP ${res.status}`, code: NON_JSON_ERROR_RESPONSE };
  }
  const text = await res.text().catch(() => "");
  const plainText = text
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return {
    message: (plainText || res.statusText || `HTTP ${res.status}`).slice(0, 300),
    code: NON_JSON_ERROR_RESPONSE,
  };
}

function isCsrfMismatch(res: Response, error: { message?: string; error?: string }): boolean {
  return res.status === 403 && (error.error === "CSRF token mismatch" || error.message === "CSRF token mismatch");
}

async function refreshCsrfToken(): Promise<boolean> {
  const res = await fetch(`${API_BASE}/auth/me`, { credentials: "include" });
  if (!res.ok) return false;
  const data = await res.json().catch(() => null) as { csrfToken?: string } | null;
  if (!data?.csrfToken) return false;
  setCsrfToken(data.csrfToken);
  return true;
}

const SSO_REENTRY_PATHS = new Set(["/auth/sso/start", "/auth/sso/pending-mfa"]);

async function request<T>(
  path: string,
  options?: RequestInit,
  retriedCsrf = false,
  retriedStalePlatformSession = false,
): Promise<T> {
  const isFormData = options?.body instanceof FormData;
  const headers: Record<string, string> = isFormData
    ? {} // Let browser set Content-Type with boundary
    : { "Content-Type": "application/json" };
  // Attach CSRF token for state-changing requests
  const method = options?.method?.toUpperCase() ?? "GET";
  if (csrfToken && method !== "GET" && method !== "HEAD") {
    headers["X-CSRF-Token"] = csrfToken;
  }
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...options,
    headers: { ...headers, ...options?.headers },
  });
  if (res.status === 401 && !path.startsWith("/auth/")) {
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const redirect = current && current !== "/login" ? `?redirect=${encodeURIComponent(current)}` : "";
    window.location.href = `/login${redirect}`;
    throw new ApiError("Unauthorized", 401);
  }
  if (!res.ok) {
    const error = await parseErrorResponse(res);
    // The dedicated admin cookie can outlive its server-side session. The
    // server clears it with this 401; repeat only the SAML entry request once
    // so re-login can continue with a fresh anonymous session.
    if (
      !retriedStalePlatformSession
      && SSO_REENTRY_PATHS.has(path)
      && res.status === 401
      && error.code === "PLATFORM_ADMIN_SESSION_COOKIE_INVALID"
    ) {
      return request<T>(path, options, retriedCsrf, true);
    }
    if (!retriedCsrf && isCsrfMismatch(res, error) && await refreshCsrfToken()) {
      return request<T>(path, options, true, retriedStalePlatformSession);
    }
    throw new ApiError(
      error.message || error.error || `HTTP ${res.status}`,
      res.status,
      error.code ?? error.error,
      error.headers,
    );
  }
  return res.json();
}

export type BlobResponse = { blob: Blob; headers: Headers };

async function requestBlobResponse(path: string, options?: RequestInit, retriedCsrf = false): Promise<BlobResponse> {
  const isFormData = options?.body instanceof FormData;
  const headers: Record<string, string> = isFormData
    ? {}
    : { "Content-Type": "application/json" };
  const method = options?.method?.toUpperCase() ?? "GET";
  if (csrfToken && method !== "GET" && method !== "HEAD") {
    headers["X-CSRF-Token"] = csrfToken;
  }
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...options,
    headers: { ...headers, ...options?.headers },
  });
  if (res.status === 401 && !path.startsWith("/auth/")) {
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const redirect = current && current !== "/login" ? `?redirect=${encodeURIComponent(current)}` : "";
    window.location.href = `/login${redirect}`;
    throw new ApiError("Unauthorized", 401);
  }
  if (!res.ok) {
    const error = await parseErrorResponse(res);
    if (!retriedCsrf && isCsrfMismatch(res, error) && await refreshCsrfToken()) {
      return requestBlobResponse(path, options, true);
    }
    throw new ApiError(
      error.message || error.error || `HTTP ${res.status}`,
      res.status,
      error.code ?? error.error,
      error.headers,
    );
  }
  return { blob: await res.blob(), headers: res.headers };
}

async function requestBlob(path: string, options?: RequestInit): Promise<Blob> {
  return (await requestBlobResponse(path, options)).blob;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  getWithHeaders: <T>(path: string, headers: Record<string, string>) =>
    request<T>(path, { headers }),
  getBlob: (path: string) => requestBlob(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body) }),
  postWithHeaders: <T>(path: string, body: unknown, headers: Record<string, string>) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body), headers }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  putWithHeaders: <T>(path: string, body: unknown, headers: Record<string, string>) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body), headers }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  patchWithHeaders: <T>(path: string, body: unknown, headers: Record<string, string>) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body), headers }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  deleteWithHeaders: <T>(path: string, headers: Record<string, string>) =>
    request<T>(path, { method: "DELETE", headers }),
  upload: <T>(path: string, formData: FormData, extraHeaders?: HeadersInit) =>
    request<T>(path, {
      method: "POST",
      body: formData,
      // Content-Type はブラウザに boundary を設定させ、冪等性などの追加ヘッダーだけ許可する。
      headers: extraHeaders ?? {},
    }),
  postBlob: (path: string, body: unknown) =>
    requestBlob(path, { method: "POST", body: JSON.stringify(body) }),
  uploadBlobResponse: (path: string, formData: FormData, extraHeaders?: HeadersInit) =>
    requestBlobResponse(path, { method: "POST", body: formData, headers: extraHeaders ?? {} }),
};
